import { DiscountCalculator } from '@studyportals/iu-global-access-initiative/DiscountCalculator';
import { ITuitionFeeDiscountCalculator } from '../../interfaces';
import store from '@/study-search/store';
import { AttendanceFilterOptionValue, DegreeTypeFilterOptionValue } from '@studyportals/search-filters/server-side';
import { IProgrammeCard } from '@studyportals/domain-client';
import { CountryTier } from '@studyportals/iu-global-access-initiative/definitions/CountryTier';
import { DiscountInformation } from '@studyportals/iu-global-access-initiative/interfaces/DiscountInformation';
import { DiscountCriteria } from '@studyportals/iu-global-access-initiative/interfaces/DiscountCriteria';
import { Deployment } from '@/study-search/utils/Deployment';

export default class TuitionFeeDiscountCalculator implements ITuitionFeeDiscountCalculator {
	private discountCalculator: DiscountCalculator;

	constructor() {
		this.discountCalculator = new DiscountCalculator();
	}

	public shouldHideTuitionFeeWithDiscount(card: IProgrammeCard, educationLevel: string): boolean {
		return store.getters.isGAIQuestionnaireTaken() && this.getCountryTier(card, educationLevel) === CountryTier.INELIGIBLE;
	}

	public getDiscountInformation(card: IProgrammeCard, educationLevel: string, canShowIUDiscountInfo = true): DiscountInformation {
		if (!canShowIUDiscountInfo) {
			return {
				percentage: 0,
				countryTier: CountryTier.INELIGIBLE,
			};
		}

		const countryId = this.getCountryIdForDiscount();
		const credits = this.getCreditsCriteria(card);

		const discountCriteria: DiscountCriteria = {
			countryId,
			deliveryMethod: this.getDeliveryMethodCriteria(card),
			degreeType: this.getDegreeTypeCriteria(card),
			educationLevel,
			credits,
		};

		const hasDuration = card.canShowDuration();
		if (hasDuration) {
			discountCriteria.durationAmount = card.getDuration().getAmount();
			discountCriteria.durationPeriod = card.getDuration().getPeriod();
		}

		return this.discountCalculator.getDiscountPercentage(discountCriteria);
	}

	private getCountryIdForDiscount(): number {
		const countryOfResidence = store.getters.getUserCountryOfResidence();
		if (countryOfResidence > 0) {
			return countryOfResidence;
		}

		const nationalityCountry = Number(store.getters.getUserCountryId());
		if (!isNaN(nationalityCountry)) {
			return nationalityCountry;
		}

		return 0;
	}

	public getCreditsCriteria(card: IProgrammeCard): number | undefined {
		if (Deployment.hasDomainApiGetEctsMember(card)) {
			const credits = card.getEcts();
			return credits === null ? undefined : credits;
		}
		return undefined;
	}

	public getDeliveryMethodCriteria(card: IProgrammeCard): string {
		if (card?.isOnline?.()) {
			return AttendanceFilterOptionValue.ONLINE;
		}

		if (card?.isBlended?.()) {
			return AttendanceFilterOptionValue.BLENDED;
		}

		return AttendanceFilterOptionValue.FACE_2_FACE;
	}

	public getDegreeTypeCriteria(card: IProgrammeCard): string | undefined {
		if (card?.getDegree?.() === 'M.B.A.') {
			return DegreeTypeFilterOptionValue.MBA;
		}

		return undefined;
	}

	private getCountryTier(card: IProgrammeCard, educationLevel: string): CountryTier {
		return this.getDiscountInformation(card, educationLevel).countryTier;
	}
}
